import * as ANALYTICS_CONSTS from '../constants/google-analytics';
import * as AnalyticsUtils from './analytics-utils';
import * as EngagementTracker from './engagement-tracker';

const isBrowser = typeof window !== 'undefined';
const FEATURE_INTERACTION_EVENT_NAME = 'feature_interaction';
const FEATURE_OUTCOME_EVENT_NAME = 'feature_outcome';
const FEATURE_PAGE_VIEW_EVENT_NAME = 'feature_page_view';

const getGoogleAnalyticsDataLayer = () => {
  if (!isBrowser) return null;

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Installs a mock window.gtag that queues events to dataLayer when the real
 * GA library hasn't been loaded yet. If gtag.js loads later it replaces this
 * stub with its own implementation and processes the queued events.
 * This is the same initializer pattern used in Google's own analytics snippet.
 */
if (isBrowser) {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args) {
      window.dataLayer.push(args);
    };
  }
}

/**
 * Low-level GA tag sender.
 * Uses window.gtag when available, otherwise pushes to dataLayer.
 */
export const sendGoogleAnalyticsTag = (...args) => {
  if (!isBrowser) return false;

  if (typeof window.gtag === 'function') {
    window.gtag(...args);
    return true;
  }

  const dataLayer = getGoogleAnalyticsDataLayer();
  if (!dataLayer) return false;

  dataLayer.push(args);
  return true;
};

export const sendGoogleAnalyticsEvent = (eventName, payload = {}) =>
  sendGoogleAnalyticsTag('event', eventName, {
    ...payload,
    engagement_time_msec: EngagementTracker.getEngagementDelta()
  });

export const setGoogleAnalyticsValue = (key, value) => sendGoogleAnalyticsTag('set', key, value);

export const setGoogleAnalyticsUserId = userId => setGoogleAnalyticsValue('user_id', userId);

export const clearGoogleAnalyticsUserId = () => setGoogleAnalyticsUserId(null);

export const setGoogleAnalyticsUserProperties = userProperties =>
  setGoogleAnalyticsValue('user_properties', userProperties || {});

const getPayloadMetadata = payload =>
  AnalyticsUtils.buildFeatureUsageMetadata(
    payload?.[ANALYTICS_CONSTS.META_DATA] || payload?.metaData || payload?.metadata || payload?.meta || {}
  );

const getFeatureUsageEventName = metadata => {
  switch (metadata.event_name) {
    case FEATURE_OUTCOME_EVENT_NAME:
      return FEATURE_OUTCOME_EVENT_NAME;
    case FEATURE_PAGE_VIEW_EVENT_NAME:
      return FEATURE_PAGE_VIEW_EVENT_NAME;
    default:
      return FEATURE_INTERACTION_EVENT_NAME;
  }
};

const getCurrentPath = () => AnalyticsUtils.sanitizeAnalyticsPath(window.location.pathname);

// Returns the full page URL as the browser sees it (matching GA4's built-in page_view),
// with query string and hash stripped but the app base path (e.g. /argos/) preserved.
const getCurrentUrl = () => window.location.href.split('?')[0].split('#')[0] || 'None';

const getReferrerPath = () => AnalyticsUtils.sanitizeAnalyticsUrl(document.referrer || '/');

export const trackGoogleAnalyticsEvent = payload => {
  if (!isBrowser) {
    return false;
  }

  const category = payload?.[ANALYTICS_CONSTS.GA_CATEGORY] || payload?.category;
  const action = payload?.[ANALYTICS_CONSTS.GA_ACTION] || payload?.action;

  if (!category || !action) {
    return false;
  }

  const metadata = getPayloadMetadata(payload);
  const currentUrl = getCurrentUrl();
  const currentPath = getCurrentPath();
  const referrerPath = getReferrerPath();
  const pathSegment = currentPath.split('/').filter(Boolean)[0];
  const featureName = metadata.feature_name || AnalyticsUtils.normalizeAnalyticsValue(currentPath, 'unknown_screen');

  sendGoogleAnalyticsEvent('argos_custom_event', {
    custom_event: action,
    screen_name: featureName,
    page_location: currentUrl,
    page_referrer: referrerPath || 'None',
    ...metadata
  });

  if (!metadata.feature_name) {
    return true;
  }

  sendGoogleAnalyticsEvent(getFeatureUsageEventName(metadata), {
    feature_area: metadata.feature_area || AnalyticsUtils.normalizeAnalyticsValue(category, 'general'),
    feature_name: featureName,
    feature_action: metadata.feature_action || AnalyticsUtils.normalizeAnalyticsValue(action, 'interact'),
    screen_id: metadata.screen_id || AnalyticsUtils.normalizeAnalyticsValue(currentPath, 'unknown_screen'),
    source_surface: metadata.source_surface || 'web_app',
    legacy_action: action,
    legacy_category: category,
    ...(pathSegment
      ? { entry_point: metadata.entry_point || AnalyticsUtils.normalizeAnalyticsValue(pathSegment) }
      : {}),
    ...(metadata.outcome ? { outcome: metadata.outcome } : {}),
    ...(metadata.report_type ? { report_type: metadata.report_type } : {})
  });

  return true;
};
